import React, { Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CartProvider } from "@/data/cart";
import { I18nProvider, useI18n } from "@/i18n/I18nContext";
import { SUPPORTED_LOCALES } from "@/i18n/translations";
import Header from "@/components/layout/Header";
import HrefLangTags from "@/components/HrefLangTags";
import ScrollToTop from "@/components/ScrollToTop";
import ChunkReloadBoundary from "@/components/ChunkReloadBoundary";
import PageFallback from "@/components/PageFallback";
import Index from "./pages/Index";

/* Deferred UI — not needed for first paint. Keeping these lazy also
   keeps `vendor-supabase` and the admin banner off the critical path. */
const Toaster = React.lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = React.lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const Footer = React.lazy(() => import("@/components/layout/Footer"));
const CartDrawer = React.lazy(() => import("@/components/CartDrawer"));
const AdminOnlyBanner = React.lazy(() => import("@/components/AdminOnlyBanner"));
/* AppEffects owns useWelcomeBonusFallback + useCampaignTracking. Loading
   it lazily keeps the supabase client out of the initial public bundle. */
const AppEffects = React.lazy(() => import("@/components/AppEffects"));
const Shop = React.lazy(() => import("./pages/Shop"));
const Category = React.lazy(() => import("./pages/Category"));
const ProductDetail = React.lazy(() => import("./pages/ProductDetail"));
const Cart = React.lazy(() => import("./pages/Cart"));
const Checkout = React.lazy(() => import("./pages/Checkout"));
const CheckoutSuccess = React.lazy(() => import("./pages/CheckoutSuccess"));
const Loyalty = React.lazy(() => import("./pages/Loyalty"));
const Promo = React.lazy(() => import("./pages/Promo"));
const RefundPolicy = React.lazy(() => import("./pages/RefundPolicy"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const CGV = React.lazy(() => import("./pages/CGV"));
const Account = React.lazy(() => import("./pages/Account"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const Reviews = React.lazy(() => import("./pages/Reviews"));
const Search = React.lazy(() => import("./pages/Search"));
const HowToPlay = React.lazy(() => import("./pages/HowToPlay"));
const RarityGuide = React.lazy(() => import("./pages/RarityGuide"));
const PriceGuide = React.lazy(() => import("./pages/PriceGuide"));
const Join = React.lazy(() => import("./pages/Join"));
const OAuthConsent = React.lazy(() => import("./pages/OAuthConsent"));

// Auth pages
const Login = React.lazy(() => import("./pages/auth/Login"));
const Signup = React.lazy(() => import("./pages/auth/Signup"));
const ForgotPassword = React.lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/auth/ResetPassword"));
const EmailConfirmed = React.lazy(() => import("./pages/auth/EmailConfirmed"));
const EmailChanged = React.lazy(() => import("./pages/auth/EmailChanged"));

// Account sub-pages
const Orders = React.lazy(() => import("./pages/account/Orders"));
const OrderDetail = React.lazy(() => import("./pages/account/OrderDetail"));
const Addresses = React.lazy(() => import("./pages/account/Addresses"));
const AccountSettings = React.lazy(() => import("./pages/account/Settings"));
const Social = React.lazy(() => import("./pages/account/Social"));
const OrderReview = React.lazy(() => import("./pages/account/OrderReview"));
const BerryHistory = React.lazy(() => import("./pages/account/BerryHistory"));
const Referral = React.lazy(() => import("./pages/account/Referral"));

// Lazy-loaded admin pages
const AdminGuard = React.lazy(() => import("./components/AdminGuard"));
const AdminOrders = React.lazy(() => import("./pages/admin/Orders"));
const AdminOrderDetail = React.lazy(() => import("./pages/admin/OrderDetail"));
const AdminEmailTest = React.lazy(() => import("./pages/admin/EmailTest"));
const AdminEmailTemplates = React.lazy(() => import("./pages/admin/EmailTemplates"));
const AdminEmailTemplateEditor = React.lazy(() => import("./pages/admin/EmailTemplateEditor"));
const AdminLoyaltyGifts = React.lazy(() => import("./pages/admin/LoyaltyGifts"));
const AdminInventory = React.lazy(() => import("./pages/admin/InventoryV2"));
const AdminCatalog = React.lazy(() => import("./pages/admin/Catalog"));
const AdminCatalogForm = React.lazy(() => import("./pages/admin/CatalogForm"));
const AdminDevTools = React.lazy(() => import("./pages/admin/DevTools"));
const AdminWelcomeBonusDiagnostics = React.lazy(() => import("./pages/admin/WelcomeBonusDiagnostics"));
const AdminShippingRules = React.lazy(() => import("./pages/admin/ShippingRules"));
const AdminNewsletter = React.lazy(() => import("./pages/admin/Newsletter"));
const AdminSocialRewards = React.lazy(() => import("./pages/admin/SocialRewards"));
const AdminOrderReviews = React.lazy(() => import("./pages/admin/OrderReviews"));
const AdminBadges = React.lazy(() => import("./pages/admin/Badges"));
const AdminCampaignDashboard = React.lazy(() => import("./pages/admin/CampaignDashboard"));
const AdminCampaignConfig = React.lazy(() => import("./pages/admin/CampaignConfig"));

// Marketing Center was removed; old admin links redirect to the banner section.
const LegacyMarketingRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { localePath } = useI18n();
  return <Navigate to={localePath(to)} replace />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,           // 1 min — avoids immediate refetch on nav
      gcTime: 5 * 60_000,          // keep cache 5 min for back/forward
      refetchOnWindowFocus: false, // avoids refetch storms
      retry: 1,
    },
  },
});

/** All locale-prefixed routes */
const LocaleRoutes = () => (
  <>
    <Route index element={<Index />} />
    <Route path="shop" element={<Suspense fallback={<PageFallback />}><Shop /></Suspense>} />
    <Route path="category/:slug" element={<Suspense fallback={<PageFallback />}><Category /></Suspense>} />
    <Route path="product/:slug" element={<Suspense fallback={<PageFallback />}><ProductDetail /></Suspense>} />
    <Route path="cart" element={<Suspense fallback={<PageFallback />}><Cart /></Suspense>} />
    <Route path="checkout" element={<Suspense fallback={<PageFallback />}><Checkout /></Suspense>} />
    <Route path="checkout/success" element={<Suspense fallback={<PageFallback />}><CheckoutSuccess /></Suspense>} />
    <Route path="loyalty" element={<Suspense fallback={<PageFallback />}><Loyalty /></Suspense>} />
    <Route path="promo" element={<Suspense fallback={<PageFallback />}><Promo /></Suspense>} />
    <Route path="refund-policy" element={<Suspense fallback={<PageFallback />}><RefundPolicy /></Suspense>} />
    <Route path="privacy-policy" element={<Suspense fallback={<PageFallback />}><PrivacyPolicy /></Suspense>} />
    <Route path="terms" element={<Suspense fallback={<PageFallback />}><CGV /></Suspense>} />
    <Route path="reviews" element={<Suspense fallback={<PageFallback />}><Reviews /></Suspense>} />
    <Route path="search" element={<Suspense fallback={<PageFallback />}><Search /></Suspense>} />
    <Route path="how-to-play" element={<Suspense fallback={<PageFallback />}><HowToPlay /></Suspense>} />
    <Route path="rarity-guide" element={<Suspense fallback={<PageFallback />}><RarityGuide /></Suspense>} />
    <Route path="price-guide" element={<Suspense fallback={<PageFallback />}><PriceGuide /></Suspense>} />
    <Route path="join" element={<Suspense fallback={<PageFallback />}><Join /></Suspense>} />
    {/* Auth */}
    <Route path="account/login" element={<Suspense fallback={<PageFallback />}><Login /></Suspense>} />
    <Route path="account/signup" element={<Suspense fallback={<PageFallback />}><Signup /></Suspense>} />
    <Route path="account/forgot-password" element={<Suspense fallback={<PageFallback />}><ForgotPassword /></Suspense>} />
    <Route path="auth/confirmed" element={<Suspense fallback={<PageFallback />}><EmailConfirmed /></Suspense>} />
    <Route path="auth/email-changed" element={<Suspense fallback={<PageFallback />}><EmailChanged /></Suspense>} />
    {/* Account */}
    <Route path="account" element={<Suspense fallback={<PageFallback />}><Account /></Suspense>} />
    <Route path="account/orders" element={<Suspense fallback={<PageFallback />}><Orders /></Suspense>} />
    <Route path="account/orders/:id" element={<Suspense fallback={<PageFallback />}><OrderDetail /></Suspense>} />
    <Route path="account/addresses" element={<Suspense fallback={<PageFallback />}><Addresses /></Suspense>} />
    <Route path="account/settings" element={<Suspense fallback={<PageFallback />}><AccountSettings /></Suspense>} />
    <Route path="account/social" element={<Suspense fallback={<PageFallback />}><Social /></Suspense>} />
    <Route path="account/referral" element={<Suspense fallback={<PageFallback />}><Referral /></Suspense>} />
    <Route path="account/orders/:id/review" element={<Suspense fallback={<PageFallback />}><OrderReview /></Suspense>} />
    <Route path="account/berry-history" element={<Suspense fallback={<PageFallback />}><BerryHistory /></Suspense>} />
    <Route path="account/loyalty" element={<Suspense fallback={<PageFallback />}><Loyalty /></Suspense>} />
    <Route path="admin/orders" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminOrders /></AdminGuard></Suspense>} />
    <Route path="admin/orders/:id" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminOrderDetail /></AdminGuard></Suspense>} />
    <Route path="admin/inventory" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminInventory /></AdminGuard></Suspense>} />
    <Route path="admin/inventory-v2" element={<Navigate to="../admin/inventory" replace />} />
    <Route path="admin/email-test" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminEmailTest /></AdminGuard></Suspense>} />
    <Route path="admin/emails" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminEmailTemplates /></AdminGuard></Suspense>} />
    <Route path="admin/emails/:id" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminEmailTemplateEditor /></AdminGuard></Suspense>} />

    <Route path="admin/loyalty-gifts" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminLoyaltyGifts /></AdminGuard></Suspense>} />
    <Route path="admin/catalog" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminCatalog /></AdminGuard></Suspense>} />
    <Route path="admin/catalog/:id" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminCatalogForm /></AdminGuard></Suspense>} />
    <Route path="admin/dev-tools" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminDevTools /></AdminGuard></Suspense>} />
    <Route path="admin/dev-tools/welcome-bonus-diagnostics" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminWelcomeBonusDiagnostics /></AdminGuard></Suspense>} />
    <Route path="admin/shipping-rules" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminShippingRules /></AdminGuard></Suspense>} />
    <Route path="admin/newsletter" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminNewsletter /></AdminGuard></Suspense>} />
    <Route path="admin/social-rewards" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminSocialRewards /></AdminGuard></Suspense>} />
    <Route path="admin/order-reviews" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminOrderReviews /></AdminGuard></Suspense>} />
    <Route path="admin/badges" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminBadges /></AdminGuard></Suspense>} />
    {/* New-member banner (SI-ACCOUNT-01) — standalone admin section */}
    <Route path="admin/new-member-banner" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminCampaignDashboard /></AdminGuard></Suspense>} />
    <Route path="admin/new-member-banner/config" element={<Suspense fallback={<PageFallback />}><AdminGuard><AdminCampaignConfig /></AdminGuard></Suspense>} />
    {/* Legacy Marketing Center routes — module removed, keep links working */}
    <Route path="admin/marketing/campaigns/si-account-01/config" element={<LegacyMarketingRedirect to="/admin/new-member-banner/config" />} />
    <Route path="admin/marketing/*" element={<LegacyMarketingRedirect to="/admin/new-member-banner" />} />

  </>
);

const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <I18nProvider>
         <AppShell>
          <CartProvider>
            <ChunkReloadBoundary fallback={null}><Suspense fallback={null}><Toaster /></Suspense></ChunkReloadBoundary>
            <ChunkReloadBoundary fallback={null}><Suspense fallback={null}><Sonner /></Suspense></ChunkReloadBoundary>
            <HrefLangTags />
            <ScrollToTop />
              <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-primary focus:text-primary-foreground focus:font-semibold focus:text-sm focus:shadow-lg">
                Aller au contenu
              </a>
              <div className="relative z-10 flex flex-col min-h-screen">
              <Header />
              <div className="flex-1" id="main-content">
                <ChunkReloadBoundary>
                  <Routes>
                    {SUPPORTED_LOCALES.map(locale => (
                      <Route key={locale} path={`/${locale}/*`}>
                        {LocaleRoutes()}
                      </Route>
                    ))}
                    <Route path="/" element={<Navigate to="/fr" replace />} />
                    {/* Reset password — non-locale route for Supabase redirect */}
                    <Route path="/reset-password" element={<Suspense fallback={<PageFallback />}><ResetPassword /></Suspense>} />
                    {/* OAuth 2.1 consent screen for MCP / agent integrations */}
                    <Route path="/.lovable/oauth/consent" element={<Suspense fallback={<PageFallback />}><OAuthConsent /></Suspense>} />
                    {/* Legacy redirects */}
                    <Route path="/shop" element={<Navigate to="/fr/shop" replace />} />
                    <Route path="/category/:slug" element={<Navigate to="/fr/category/:slug" replace />} />
                    <Route path="/product/:slug" element={<Navigate to="/fr/product/:slug" replace />} />
                    <Route path="/cart" element={<Navigate to="/fr/cart" replace />} />
                    <Route path="/checkout" element={<Navigate to="/fr/checkout" replace />} />
                    <Route path="/loyalty" element={<Navigate to="/fr/loyalty" replace />} />
                    <Route path="*" element={<Suspense fallback={<PageFallback />}><NotFound /></Suspense>} />
                  </Routes>
                </ChunkReloadBoundary>
              </div>
              <ChunkReloadBoundary fallback={null}><Suspense fallback={<footer className="mt-20" style={{ minHeight: 416 }} />}><Footer /></Suspense></ChunkReloadBoundary>
            </div>
            <ChunkReloadBoundary fallback={null}><Suspense fallback={null}><CartDrawer /></Suspense></ChunkReloadBoundary>
            <ChunkReloadBoundary fallback={null}><Suspense fallback={null}><AppEffects /></Suspense></ChunkReloadBoundary>
            <ChunkReloadBoundary fallback={null}><Suspense fallback={null}><AdminOnlyBanner /></Suspense></ChunkReloadBoundary>
          </CartProvider>
         </AppShell>
        </I18nProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
